import {Box, Button, Card, CardContent, Typography} from "@mui/material";
import PasswordInput from "auth/PasswordInput";
import {usePasswordResetMutation} from "auth/api";
import {validatePassword} from "auth/utils/validators";
import AppDialog from "components/AppDialog";
import {useAuth} from "hooks/useAuth";
import useForm, {type FieldConfig} from "hooks/useForm";
import type {FC} from "react";
import {handleMutation} from "services/http/utils";
import {handleError} from "services/notification/handlers";

type ResetPasswordDialogProps = {
  open: boolean;
  onClose: () => void;
};
const ResetPasswordDialog: FC<ResetPasswordDialogProps> = ({open, onClose}) => {
  const {clearUser} = useAuth();

  const [resetPassword, {loading, error}] = usePasswordResetMutation();

  const initialFields: Record<string, FieldConfig<any>> = {
    password: ["", () => ""],
    newPassword: ["", validatePassword],
    confirmNewPassword: ["", () => ""],
  };

  const {fields, handleSubmit, resetFields, setFieldError} = useForm({
    fields: initialFields,
    onSubmit: async values => {
      if (values.newPassword !== values.confirmNewPassword) {
        setFieldError("confirmPassword", "Passwords do not match");
        return;
      }

      const payload = {
        password: values.password,
        new_password: values.newPassword,
      };

      await handleMutation(
        resetPassword,
        payload,
        "Password reset successfully, please login to continue!",
        undefined,
        {
          onSuccess: () => clearUser(),
        },
      );
    },
  });

  const handleClose = () => {
    onClose();
    resetFields();
  };

  if (error) handleError(error);

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title="Reset Password"
      description="Type in your password and new password."
      maxWidth="sm"
      actions={<Button onClick={handleClose}>Close</Button>}>
      <Box display="flex" alignItems="center" justifyContent="center" px={2}>
        <form onSubmit={handleSubmit} style={{width: "100%", maxWidth: 400}}>
          <Card elevation={0} title="Reset your Password">
            <CardContent>
              <Typography variant="h5" align="center" gutterBottom>
                Reset your Password
              </Typography>
              <PasswordInput label="Old Password" {...fields.password} />
              <PasswordInput label="New Password" {...fields.newPassword} />
              <PasswordInput
                label="Confirm New Password"
                {...fields.confirmNewPassword}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                loading={loading}
                sx={{mt: 3}}>
                Reset Password
              </Button>
            </CardContent>
          </Card>
        </form>
      </Box>
    </AppDialog>
  );
};

export default ResetPasswordDialog;
